import React from 'react';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { IApiConfig, useApi } from '../../../../hooks/useApi';
import { useUser } from '../../../../hooks/useUser';
import {
    IdpConnection,
    IdpConnectionUpdate,
    NOT_EDITABLE_REASONS,
    NotEditableReason,
    UPDATE_FAILURE_REASONS,
    UpdateFailureReason,
    UpdateState,
} from '../identityProviderManagement.types';

export interface UseIdpConnectionResult {
    /** The connection's editable fields, once read. */
    connection?: IdpConnection;
    loading: boolean;
    loadFailed: boolean;
    state: UpdateState;
    /** Fire-and-forget, like the add hook: the outcome arrives through `state`. */
    save: (input: IdpConnectionUpdate) => void;
    reset: () => void;
    failureReason?: UpdateFailureReason | NotEditableReason;
    outcomeUnknown: boolean;
}

type KnownFailureReason = UpdateFailureReason | NotEditableReason;

const KNOWN_FAILURE_REASONS: readonly KnownFailureReason[] = [
    ...UPDATE_FAILURE_REASONS,
    ...NOT_EDITABLE_REASONS,
];

const isKnownFailureReason = (
    details: unknown,
): details is KnownFailureReason =>
    KNOWN_FAILURE_REASONS.includes(details as KnownFailureReason);

const reasonFrom = (details: unknown): KnownFailureReason | undefined =>
    isKnownFailureReason(details) ? details : undefined;

/**
 * Read and update one identity provider's Auth0 connection.
 *
 * Keyed by CONNECTION NAME, not provider id: the connection belongs to
 * `bb-auth-broker-provisioner`, and `bb-identity-provider` captures none of the
 * vendor-facing configuration, so the metadata URL an edit form needs is absent
 * from the list response and must be read here.
 *
 * The update is synchronous — a PATCH returning the changed connection — unlike
 * create, which runs a Step Function and is polled.
 */
export const useIdpConnection = (
    connectionName: string | undefined,
    enabled: boolean,
): UseIdpConnectionResult => {
    const { api } = useAppConfigContext();
    const user = useUser();
    const tenantId = user?.tenantIdentifier;

    const base = `${api?.authProv}/v1/data/tenants/${tenantId}/identityProviders/connections`;
    const url = `${base}/${encodeURIComponent(connectionName ?? '')}`;

    const [state, setState] = React.useState<UpdateState>(UpdateState.IDLE);
    const [failureReason, setFailureReason] = React.useState<
        UpdateFailureReason | NotEditableReason
    >();
    const [outcomeUnknown, setOutcomeUnknown] = React.useState(false);

    const readConfig = React.useMemo<IApiConfig>(
        () => ({
            url,
            method: 'GET',
            useAuth: true,
            executeType: 'manual',
            useCache: false,
        }),
        [url],
    );
    const {
        data: connection,
        error: readError,
        loading,
        execute: read,
        reset: resetRead,
    } = useApi<IdpConnection>(readConfig);

    const requestedFor = React.useRef<string>();
    React.useEffect(() => {
        if (!enabled) {
            requestedFor.current = undefined;
            return;
        }
        if (!connectionName || requestedFor.current === connectionName) return;
        requestedFor.current = connectionName;
        resetRead();
        read();
    }, [enabled, connectionName, read, resetRead]);

    const saveConfig = React.useMemo<IApiConfig>(
        () => ({
            url,
            method: 'PATCH',
            useAuth: true,
            executeType: 'manual',
            useCache: false,
        }),
        [url],
    );
    const {
        data: saved,
        error: saveError,
        loading: saving,
        execute: patch,
    } = useApi<IdpConnection>(saveConfig);

    const save = React.useCallback(
        (input: IdpConnectionUpdate) => {
            setState(UpdateState.SAVING);
            setFailureReason(undefined);
            setOutcomeUnknown(false);
            patch({
                url,
                method: 'PATCH',
                data: {
                    displayName: input.displayName,
                    metadataUrl: input.metadataUrl,
                    mapping: input.mapping,
                },
            });
        },
        [patch, url],
    );

    React.useEffect(() => {
        if (state !== UpdateState.SAVING || saving) return;
        if (saveError) {
            const response = (
                saveError as {
                    response?: {
                        status?: number;
                        data?: { details?: unknown };
                    };
                }
            )?.response;
            const reason = reasonFrom(response?.data?.details);
            const unresolved =
                response?.status === undefined ||
                reason === 'BROKER_UNAVAILABLE';
            setOutcomeUnknown(unresolved);
            setFailureReason(reason);
            setState(UpdateState.FAILED);
            if (unresolved) read();
            return;
        }
        if (saved) setState(UpdateState.SAVED);
    }, [saved, saveError, saving, state, read]);

    const reset = React.useCallback(() => {
        setState(UpdateState.IDLE);
        setFailureReason(undefined);
        setOutcomeUnknown(false);
    }, []);

    return {
        connection,
        loading: !!loading,
        // No connection name means no request to make, so the dialog reports a
        // failed load rather than spinning on a read that never starts.
        loadFailed: !!readError || (enabled && !connectionName),
        state,
        save,
        reset,
        failureReason,
        outcomeUnknown,
    };
};
